import React, { useCallback, useEffect, useState } from "react";
import { CalendarOff, Loader2, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, formatDate } from "@/lib/api";

const today = () => new Date(Date.now() + 5.5 * 3600e3).toISOString().slice(0, 10);

function Chip({ on, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${on ? "bg-blue-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
      {children}
    </button>
  );
}

function HolidayForm({ isAdmin, classes, onSaved }) {
  const [date, setDate] = useState(today());
  const [dateTo, setDateTo] = useState(today());
  const [scope, setScope] = useState(isAdmin ? "all" : "classes");
  const [picked, setPicked] = useState([]);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const toggle = (c) => setPicked((p) => (p.includes(c) ? p.filter((x) => x !== c) : [...p, c]));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/attendance-holidays", { date, date_to: dateTo, scope, classes: scope === "all" ? [] : picked, reason });
      toast.success(`Holiday marked for ${data.created} day${data.created > 1 ? "s" : ""}${data.skipped ? ` (${data.skipped} already marked)` : ""}`);
      setReason(""); setPicked([]); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save holiday"); }
    finally { setSaving(false); }
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" data-testid="holiday-form">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div><Label className="text-xs">From</Label><Input type="date" value={date} onChange={(e) => { setDate(e.target.value); if (e.target.value > dateTo) setDateTo(e.target.value); }} data-testid="holiday-date" /></div>
        <div><Label className="text-xs">To</Label><Input type="date" value={dateTo} min={date} onChange={(e) => setDateTo(e.target.value)} data-testid="holiday-date-to" /></div>
        <div className="col-span-2 sm:col-span-1"><Label className="text-xs">Reason</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Diwali, exam day" data-testid="holiday-reason" /></div>
      </div>
      {isAdmin && (
        <div className="flex gap-2">
          <Chip on={scope === "all"} onClick={() => setScope("all")} testid="holiday-scope-all">Whole institute</Chip>
          <Chip on={scope === "classes"} onClick={() => setScope("classes")} testid="holiday-scope-classes">Chosen classes</Chip>
        </div>
      )}
      {scope === "classes" && (
        <div className="flex flex-wrap gap-1.5" data-testid="holiday-classes">
          {classes.length === 0 ? <p className="text-xs text-slate-500">No classes found.</p>
            : classes.map((c) => <Chip key={c} on={picked.includes(c)} onClick={() => toggle(c)} testid={`holiday-class-${c}`}>{c}</Chip>)}
        </div>
      )}
      <Button onClick={save} disabled={saving || !reason.trim() || (scope === "classes" && !picked.length)} className="bg-blue-900 hover:bg-blue-800" data-testid="holiday-save">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarOff className="h-4 w-4 mr-1" />} Mark holiday
      </Button>
    </div>
  );
}

function HolidayRow({ h, canDelete, onDelete }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3" data-testid={`holiday-row-${h.id}`}>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-slate-900">{formatDate(h.date)} · {h.reason}</p>
        <p className="text-xs text-slate-500">{h.scope === "all" ? "Whole institute" : h.classes.join(", ")} · by {h.created_by_name}{h.teacher_id ? " (teacher)" : ""}</p>
      </div>
      {canDelete && <Button variant="ghost" size="sm" onClick={() => onDelete(h.id)} data-testid={`holiday-delete-${h.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>}
    </div>
  );
}

export function AttHolidays({ isAdmin, classes, myId, onChanged }) {
  const [list, setList] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/attendance-holidays"); setList(data); }
    catch { toast.error("Could not load holidays"); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const changed = () => { load(); if (onChanged) onChanged(); };
  const del = async (id) => {
    try { await api.delete(`/attendance-holidays/${id}`); toast.success("Holiday removed"); changed(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove"); }
  };
  return (
    <div className="space-y-4" data-testid="att-holidays">
      <HolidayForm isAdmin={isAdmin} classes={classes} onSaved={changed} />
      {!list ? <Loader2 className="h-5 w-5 animate-spin text-blue-900" />
        : list.length === 0 ? <p className="text-sm text-slate-500" data-testid="holiday-empty">No holidays marked yet.</p>
        : <div className="space-y-2">{list.map((h) => <HolidayRow key={h.id} h={h} canDelete={isAdmin || h.teacher_id === myId} onDelete={del} />)}</div>}
    </div>
  );
}
